/**
 * Client Sidebar Configuration
 *
 * Curated navigation for client (customer) audience only.
 * No admin, monitoring clusters, development tools, or employee-only items.
 * Pure helpers — no localStorage (SSR-safe).
 */

import type { User } from '@/infrastructure/api';

/** Minimal item shape shared with admin sidebar manager (avoids circular runtime import). */
export interface ClientSidebarItem {
  key: string;
  label: string;
  icon: string;
  route: string;
  order: number;
  children?: ClientSidebarItem[];
}

export interface ClientSidebarConfig {
  items: ClientSidebarItem[];
  footer: ClientSidebarItem[];
}

/** Loose user shape for detection — tolerates partial / API-variant payloads. */
export type AudienceUser = (User & {
  userType?: string | null;
  audience?: string | null;
  clientData?: unknown;
  roles?: string[] | null;
  permissions?: string[] | null;
  employee_id?: string | null;
}) | null | undefined;

const CLIENT_ROLE_HINTS = new Set([
  'client',
  'customer',
  'retail_client',
  'investor',
]);

const EMPLOYEE_ROLE_HINTS = new Set([
  'employee',
  'admin',
  'super_admin',
  'staff',
  'auditor',
  'compliance_officer',
  'trader_ops',
  'ops',
]);

/**
 * Detect whether the authenticated user is a client (customer) audience.
 *
 * Priority (first decisive signal wins within each group):
 * 1. Explicit `userType` / `audience` fields
 * 2. Presence of `clientData` vs employee markers (`employee_id`, elevated clearance)
 * 3. Role hints (`client` vs `admin` / `employee` / …)
 *
 * SSR-safe: never reads localStorage or window.
 * Missing/partial fields are tolerated; defaults to non-client when ambiguous
 * without positive client signals.
 */
export function isClientAudience(user: AudienceUser): boolean {
  if (!user || typeof user !== 'object') {
    return false;
  }

  const u = user as unknown as Record<string, unknown>;

  // --- Explicit audience / type (highest confidence) ---
  const userType = normalizeString(u.userType ?? u.user_type);
  if (userType === 'client' || userType === 'customer') {
    return true;
  }
  if (userType === 'employee' || userType === 'staff' || userType === 'admin') {
    return false;
  }

  const audience = normalizeString(u.audience);
  if (audience === 'client' || audience === 'customer') {
    return true;
  }
  if (audience === 'employee' || audience === 'staff' || audience === 'internal') {
    return false;
  }

  // --- Structural markers ---
  const hasClientData =
    u.clientData != null &&
    typeof u.clientData === 'object' &&
    !Array.isArray(u.clientData);

  const hasEmployeeId =
    (typeof u.employee_id === 'string' && u.employee_id.trim().length > 0) ||
    (typeof u.employeeId === 'string' && (u.employeeId as string).trim().length > 0);

  const clearance = normalizeString(u.clearance_level);
  const elevatedClearance =
    clearance === 'confidential' ||
    clearance === 'secret' ||
    clearance === 'top_secret';

  // Strong employee markers win when type/audience were not explicit
  if (hasEmployeeId || elevatedClearance) {
    return false;
  }

  if (hasClientData) {
    return true;
  }

  // --- Roles ---
  const roles = collectStrings(u.roles, u.role);
  const hasClientRole = roles.some((r) => CLIENT_ROLE_HINTS.has(r));
  const hasEmployeeRole = roles.some((r) => EMPLOYEE_ROLE_HINTS.has(r));

  if (hasClientRole && !hasEmployeeRole) {
    return true;
  }

  return false;
}

/**
 * Locked client operator set:
 * Dashboard, Markets, Portfolio, Agents, Trade, Activity, Settings.
 *
 * Chat, Libraries, Analytics, and Help stay discoverable via search and
 * sidebar quick actions — not as peer rail items.
 */
export function getClientSidebarItems(): ClientSidebarItem[] {
  return [
    {
      key: 'client-dashboard',
      label: 'Dashboard',
      icon: 'DashboardOutlined',
      route: '/client/dashboard',
      order: 1,
    },
    {
      key: 'markets',
      icon: 'LineChartOutlined',
      label: 'Markets',
      route: '/market-data',
      order: 2,
    },
    {
      key: 'portfolio',
      label: 'Portfolio',
      icon: 'WalletOutlined',
      route: '/portfolio',
      order: 3,
    },
    {
      key: 'agents',
      label: 'Agents',
      icon: 'ThunderboltOutlined',
      route: '/ai-trading',
      order: 4,
    },
    {
      key: 'trading',
      label: 'Trade',
      icon: 'ShoppingCartOutlined',
      route: '/trading',
      order: 5,
    },
    {
      key: 'activity',
      label: 'Activity',
      icon: 'FileTextOutlined',
      route: '/activity',
      order: 6,
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: 'SettingOutlined',
      route: '/settings',
      order: 7,
    },
  ];
}

/**
 * Full client sidebar config (items + empty footer).
 */
export function getClientSidebarConfig(): ClientSidebarConfig {
  return {
    items: getClientSidebarItems(),
    footer: [],
  };
}

function normalizeString(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase();
}

function collectStrings(...sources: unknown[]): string[] {
  const out: string[] = [];
  for (const source of sources) {
    if (typeof source === 'string' && source.trim()) {
      out.push(source.trim().toLowerCase());
    } else if (Array.isArray(source)) {
      for (const item of source) {
        if (typeof item === 'string' && item.trim()) {
          out.push(item.trim().toLowerCase());
        }
      }
    }
  }
  return out;
}
